// Layout.jsx
import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './Header';
import Sidebar from './Sidebar';

const Layout = () => {
  const SIDEBAR_WIDTH = 288; // 18rem -> px
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // responsive check
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);

      // Auto-close sidebar on mobile
      if (mobile && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [isSidebarOpen]);

  const toggleSidebar = () => setIsSidebarOpen(v => !v);
  const closeSidebar = () => setIsSidebarOpen(false);

  // inline styles (match app visual language)
  const styles = {
    page: {
      minHeight: '100vh',
      background: 'var(--gray-50, #f8fafc)',
      display: 'flex',
      flexDirection: 'column'
    },
    layoutRow: {
      display: 'flex',
      flex: 1,
      position: 'relative'
    },
    main: (shift) => ({
      flex: 1,
      transition: 'all 280ms ease',
      marginLeft: shift ? `${SIDEBAR_WIDTH}px` : 0,
      minHeight: '100vh',
      padding: '1.5rem',
      boxSizing: 'border-box'
    }),
    contentWrap: {
      maxWidth: 1400,
      margin: '0 auto',
      width: '100%',
      padding: '0 1rem'
    },
    mobileOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.45)',
      zIndex: 40
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <Header onToggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />

      <div style={styles.layoutRow}>
        {/* Sidebar: pass isOpen true for desktop (not mobile) or when mobile-open */}
        <Sidebar isOpen={isSidebarOpen || !isMobile} onClose={closeSidebar} />

        {/* Mobile overlay */}
        <AnimatePresence>
          {isMobile && isSidebarOpen && (
            <motion.div
              style={styles.mobileOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeSidebar}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>

        {/* Main content area */}
        <motion.main
          style={styles.main(!isMobile)}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, delay: 0.08 }}
          aria-live="polite"
        >
          <div style={styles.contentWrap}>
            <Outlet />
          </div>
        </motion.main>
      </div>
    </div>
  );
};

export default Layout;
